import { useEffect, useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Checkbox } from '../../../components/ui/checkbox'; // Adjust path if needed

const lineOptionsInitial = [
  {
    title: 'correct',
    color: 'var(--q3-green-normal)',
    checked: true,
  },
  {
    title: 'incorrect',
    color: 'var(--q3-danger-normal)',
    checked: true,
  },
  {
    title: 'overall',
    color: 'var(--q3-neutral-light)',
    checked: true,
  },
];

export default function TimeJourneyChart({ timeJourney }) {
  const [chartData, setChartData] = useState([]);
  const [lineOptions, setLineOptions] = useState(lineOptionsInitial);

  useEffect(() => {
    if (!timeJourney || typeof timeJourney !== 'object') return;

    const entries = Object.entries(timeJourney);

    if (entries.length === 0) {
      setChartData([]);
      return;
    }

    const lastIndex = entries.length - 1;
    const [lastKey, lastValue] = entries[lastIndex];

    let cleanedEntries = [...entries];

    if (lastKey === 'undefined') {
      if (entries.length >= 2) {
        const [secondLastKey, secondLastValue] = entries[lastIndex - 1];

        const merged = {
          ...secondLastValue,
          correct: (secondLastValue.correct || 0) + (lastValue?.correct || 0),
          incorrect: (secondLastValue.incorrect || 0) + (lastValue?.incorrect || 0),
          overall: (secondLastValue.overall || 0) + (lastValue?.overall || 0),
        };

        cleanedEntries = [...entries.slice(0, lastIndex - 1), [secondLastKey, merged]];
      } else {
        cleanedEntries = entries.slice(0, -1); // remove 'undefined'
      }
    }

    const data = cleanedEntries.map(([name, values]) => ({
      name,
      ...values,
    }));

    setChartData(data);
  }, [timeJourney]);

  const handleLineToggle = (value, index) => {
    const updated = lineOptions.map((opt, i) => ({
      ...opt,
      checked: i === index ? !opt.checked : opt.checked,
    }));
    setLineOptions(updated);
  };

  const visibleLines = lineOptions.filter((item) => item.checked);

  return (
    <div className="border border-[var(--q3-stroke-light)] border-t-0 rounded-b-xl">
      <style jsx>{`
        .checkbox-custom[data-state='checked'] {
          background-color: var(--checkbox-bg) !important;
          border-color: var(--checkbox-border) !important;
        }
        .checkbox-custom:hover {
          border-color: var(--checkbox-border) !important;
        }
        .checkbox-custom:focus-visible {
          ring-color: var(--checkbox-border) !important;
        }
      `}</style>

      <div className="w-full">
        <div className="flex justify-start gap-4 flex-wrap mb-4 px-4 pt-2">
          {lineOptions.map((item, index) => (
            <div
              key={index}
              className="flex items-center cursor-pointer gap-2 border-b-2"
              style={{ borderBottomColor: item.checked ? item.color : 'transparent' }}
            >
              <Checkbox
                id={index}
                checked={item.checked}
                onCheckedChange={(value) => handleLineToggle(value, index)}
                className="checkbox-custom cursor-pointer rounded-[4px] border-[var(--q3-stroke-normal)]"
                style={{
                  '--checkbox-bg': item.color,
                  '--checkbox-border': item.color,
                }}
              />
              <label htmlFor={index.toString()} className="text-[var(--q3-neutral-default)] cursor-pointer text-sm">
                {item.title.charAt(0).toUpperCase() + item.title.slice(1)}
              </label>
            </div>
          ))}
        </div>

        <div className="w-full overflow-x-auto">
          <div className="min-w-[800px] h-[400px] pointer-events-none">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--q3-stroke-light)" />
                <XAxis
                  dataKey="name"
                  tickFormatter={(value) => `${value.replace('-', ' - ').replace('M', ' min')}`}
                  tick={{ fontSize: 12, fill: 'var(--q3-neutral-light)' }}
                  stroke="var(--q3-stroke-light)"
                />
                <YAxis
                  tickFormatter={(value) => `${value} Qs`}
                  width={50}
                  tick={{ fontSize: 12, fill: 'var(--q3-neutral-light)' }}
                  stroke="var(--q3-stroke-light)"
                />
                <Tooltip
                  formatter={(value, name) => [`${value} Qs`, name.charAt(0).toUpperCase() + name.slice(1)]}
                  labelStyle={{ color: 'var(--q3-neutral-default)', fontSize: 12 }}
                  contentStyle={{
                    borderRadius: 8,
                    border: '1px solid var(--q3-stroke-light)',
                    backgroundColor: 'var(--q3-surface-default)',
                    fontSize: 12,
                  }}
                />
                {visibleLines.map((line) => (
                  <Line
                    key={line.title}
                    type="monotone"
                    dataKey={line.title}
                    stroke={line.color}
                    strokeWidth={4}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
